import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import StarField from '../effects/StarField';

const agents = [
  { name: 'AEGIS', role: 'Servicio al Cliente', desc: 'Protege y acompaña a cada cliente 24/7.', color: '#D23CB3', icon: 'shield', eyeShape: 'round' },
  { name: 'CIPHER', role: 'Tech & Operaciones', desc: 'Automatiza y optimiza la infraestructura.', color: '#7B5EA7', icon: 'code', eyeShape: 'square' },
  { name: 'SENTINEL', role: 'Compliance & AML', desc: 'Vigilancia regulatoria sin descanso.', color: '#2ECC71', icon: 'verified_user', eyeShape: 'round' },
  { name: 'QUANTUM', role: 'Tesorería & FX', desc: 'Precisión y velocidad en cada operación.', color: '#3498DB', icon: 'currency_exchange', eyeShape: 'square' },
  { name: 'NEXUS', role: 'Onboarding & Comercial', desc: 'Guía cada nueva vinculación empresarial.', color: '#E91E8C', icon: 'handshake', eyeShape: 'round' },
  { name: 'ORACLE', role: 'Analytics & Reportes', desc: 'Inteligencia de datos para decisiones.', color: '#F39C12', icon: 'insights', eyeShape: 'square' },
];

function AgentBot({ agent, index }) {
  const c = agent.color;
  const isSquareEye = agent.eyeShape === 'square';

  const handleClick = () => {
    window.dispatchEvent(new CustomEvent('openAgentChat', { detail: { agent } }));
  };

  return (
    <motion.div
      className="flex flex-col items-center cursor-pointer group"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      onClick={handleClick}
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.97 }}
    >
      <motion.div
        className="relative w-[100px] h-[130px] md:w-[120px] md:h-[150px]"
        animate={{ y: [0, -5, 0] }}
        transition={{ duration: 3 + index * 0.3, repeat: Infinity, ease: 'easeInOut' }}
      >
        <svg viewBox="0 0 120 150" fill="none" className="w-full h-full">
          <defs>
            <linearGradient id={`body${index}`} x1="50%" y1="0%" x2="50%" y2="100%">
              <stop offset="0%" stopColor={c} />
              <stop offset="100%" stopColor={c} stopOpacity="0.7" />
            </linearGradient>
            <linearGradient id={`dark${index}`} x1="50%" y1="0%" x2="50%" y2="100%">
              <stop offset="0%" stopColor={c} stopOpacity="0.5" />
              <stop offset="100%" stopColor={c} stopOpacity="0.3" />
            </linearGradient>
            <filter id={`glow${index}`}>
              <feGaussianBlur stdDeviation="2.5" />
              <feMerge><feMergeNode /><feMergeNode in="SourceGraphic" /></feMerge>
            </filter>
          </defs>

          {/* Shadow */}
          <ellipse cx="60" cy="145" rx="30" ry="5" fill={c} opacity="0.1" />

          {/* Body */}
          <rect x="35" y="75" width="50" height="50" rx="14" fill={`url(#body${index})`} />
          {/* Chest light */}
          <motion.circle cx="60" cy="97" r="4" fill="#fff" opacity="0.25" filter={`url(#glow${index})`}
            animate={{ opacity: [0.15, 0.4, 0.15] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          />

          {/* Head */}
          <rect x="30" y="25" width="60" height="52" rx="18" fill={`url(#body${index})`} />
          {/* Visor */}
          <rect x="38" y="35" width="44" height="28" rx="10" fill="#0a090c" />

          {/* Eyes */}
          <motion.g
            animate={{ scaleY: [1, 1, 0.1, 1, 1] }}
            transition={{ duration: 4 + index * 0.5, repeat: Infinity, ease: 'easeInOut', times: [0, 0.45, 0.5, 0.55, 1] }}
            style={{ transformOrigin: '60px 48px' }}
          >
            {isSquareEye ? (
              <>
                <rect x="44" y="42" width="12" height="10" rx="3" fill={c} filter={`url(#glow${index})`} />
                <rect x="47" y="44" width="6" height="6" rx="1.5" fill="#fff" />
                <rect x="64" y="42" width="12" height="10" rx="3" fill={c} filter={`url(#glow${index})`} />
                <rect x="67" y="44" width="6" height="6" rx="1.5" fill="#fff" />
              </>
            ) : (
              <>
                <circle cx="50" cy="48" r="6" fill={c} filter={`url(#glow${index})`} />
                <circle cx="50" cy="48" r="3" fill="#fff" />
                <circle cx="70" cy="48" r="6" fill={c} filter={`url(#glow${index})`} />
                <circle cx="70" cy="48" r="3" fill="#fff" />
              </>
            )}
          </motion.g>

          {/* Antenna */}
          <line x1="60" y1="25" x2="60" y2="14" stroke={c} strokeWidth="2" strokeLinecap="round" />
          <motion.circle cx="60" cy="11" r="4" fill={c} filter={`url(#glow${index})`}
            animate={{ scale: [1, 1.3, 1], opacity: [0.6, 1, 0.6] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          />

          {/* Arms */}
          <motion.rect x="15" y="80" width="22" height="10" rx="5" fill={`url(#dark${index})`}
            animate={{ rotate: [0, 5, 0, -5, 0] }}
            transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
            style={{ transformOrigin: '35px 85px' }}
          />
          <motion.rect x="83" y="80" width="22" height="10" rx="5" fill={`url(#dark${index})`}
            animate={{ rotate: [0, -5, 0, 5, 0] }}
            transition={{ duration: 3.5, repeat: Infinity, ease: 'easeInOut' }}
            style={{ transformOrigin: '85px 85px' }}
          />

          {/* Legs */}
          <rect x="42" y="123" width="12" height="14" rx="5" fill={`url(#dark${index})`} />
          <rect x="66" y="123" width="12" height="14" rx="5" fill={`url(#dark${index})`} />
          <ellipse cx="48" cy="138" rx="9" ry="4" fill={`url(#body${index})`} />
          <ellipse cx="72" cy="138" rx="9" ry="4" fill={`url(#body${index})`} />
        </svg>
      </motion.div>

      <div className="text-center mt-2">
        <p className="text-sm font-black font-headline tracking-wider" style={{ color: c }}>{agent.name}</p>
        <p className="text-[10px] text-on-surface-variant/60 font-label uppercase tracking-widest mt-0.5">{agent.role}</p>
        <p className="text-[10px] text-on-surface-variant/40 mt-1 max-w-[140px] leading-relaxed">{agent.desc}</p>
        <p className="text-[9px] font-label uppercase tracking-wider mt-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ color: c }}>Hablar con {agent.name}</p>
      </div>
    </motion.div>
  );
}

/* ── CORTEX — The Super Brain (CrossPay logo with body) ── */
function CortexBot() {
  return (
    <motion.div
      className="flex flex-col items-center"
      initial={{ opacity: 0, scale: 0.8 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.8, type: 'spring' }}
    >
      <motion.div
        className="relative w-[140px] h-[200px] md:w-[180px] md:h-[240px]"
        animate={{ y: [0, -8, 0] }}
        transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
      >
        <svg viewBox="0 0 180 240" fill="none" className="w-full h-full">
          <defs>
            <linearGradient id="cortexBody" x1="50%" y1="0%" x2="50%" y2="100%">
              <stop offset="0%" stopColor="#A84397" />
              <stop offset="100%" stopColor="#D23CB3" />
            </linearGradient>
            <linearGradient id="cortexDark" x1="50%" y1="0%" x2="50%" y2="100%">
              <stop offset="0%" stopColor="#6b2565" />
              <stop offset="100%" stopColor="#4a1a48" />
            </linearGradient>
            <linearGradient id="cortexVisor" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#0f0a18" />
              <stop offset="100%" stopColor="#060410" />
            </linearGradient>
            <radialGradient id="cortexPulse">
              <stop offset="0%" stopColor="#D23CB3" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#A84397" stopOpacity="0" />
            </radialGradient>
            <filter id="cortexGlow">
              <feGaussianBlur stdDeviation="4" />
              <feMerge><feMergeNode /><feMergeNode in="SourceGraphic" /></feMerge>
            </filter>
            <filter id="cortexBigGlow">
              <feGaussianBlur stdDeviation="8" />
              <feMerge><feMergeNode /><feMergeNode in="SourceGraphic" /></feMerge>
            </filter>
          </defs>

          {/* Shadow */}
          <ellipse cx="90" cy="232" rx="45" ry="7" fill="rgba(210,60,179,0.12)" />

          {/* Aura / energy field */}
          <motion.circle cx="90" cy="110" r="80" fill="url(#cortexPulse)"
            animate={{ r: [70, 85, 70], opacity: [0.2, 0.08, 0.2] }}
            transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
          />

          {/* Body — larger, more armored */}
          <path d="M55 115 L60 105 L120 105 L125 115 L128 180 Q128 195 115 195 L65 195 Q52 195 52 180 Z"
            fill="url(#cortexBody)" stroke="#D23CB3" strokeWidth="0.8" strokeOpacity="0.3" />

          {/* Chest — CrossPay logo area */}
          <rect x="68" y="118" width="44" height="40" rx="8" fill="url(#cortexVisor)" stroke="#A84397" strokeWidth="0.6" strokeOpacity="0.4" />

          {/* Mini neural network on chest */}
          <motion.g animate={{ opacity: [0.4, 0.8, 0.4] }} transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}>
            <circle cx="80" cy="130" r="2" fill="#D23CB3" />
            <circle cx="100" cy="130" r="2" fill="#A84397" />
            <circle cx="90" cy="140" r="2.5" fill="#D23CB3" />
            <circle cx="80" cy="148" r="2" fill="#A84397" />
            <circle cx="100" cy="148" r="2" fill="#D23CB3" />
            <line x1="80" y1="130" x2="90" y2="140" stroke="#D23CB3" strokeWidth="0.5" opacity="0.5" />
            <line x1="100" y1="130" x2="90" y2="140" stroke="#A84397" strokeWidth="0.5" opacity="0.5" />
            <line x1="90" y1="140" x2="80" y2="148" stroke="#D23CB3" strokeWidth="0.5" opacity="0.5" />
            <line x1="90" y1="140" x2="100" y2="148" stroke="#A84397" strokeWidth="0.5" opacity="0.5" />
          </motion.g>

          {/* Status lights on body */}
          <motion.circle cx="70" cy="170" r="2" fill="#2ECC71" filter="url(#cortexGlow)"
            animate={{ opacity: [0.4, 1, 0.4] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.circle cx="80" cy="170" r="2" fill="#3498DB" filter="url(#cortexGlow)"
            animate={{ opacity: [1, 0.4, 1] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.circle cx="90" cy="170" r="2" fill="#D23CB3" filter="url(#cortexGlow)"
            animate={{ opacity: [0.4, 1, 0.4] }}
            transition={{ duration: 1.2, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.circle cx="100" cy="170" r="2" fill="#F39C12" filter="url(#cortexGlow)"
            animate={{ opacity: [1, 0.4, 1] }}
            transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.circle cx="110" cy="170" r="2" fill="#E91E8C" filter="url(#cortexGlow)"
            animate={{ opacity: [0.4, 1, 0.4] }}
            transition={{ duration: 1.3, repeat: Infinity, ease: 'easeInOut' }}
          />

          {/* Head — the CrossPay logo IS the head */}
          <rect x="48" y="35" width="84" height="72" rx="24" fill="url(#cortexBody)" stroke="#D23CB3" strokeWidth="0.8" strokeOpacity="0.3" />
          {/* Visor */}
          <rect x="56" y="45" width="68" height="45" rx="16" fill="url(#cortexVisor)" />

          {/* Antenna — crown-like, triple */}
          <line x1="75" y1="35" x2="70" y2="16" stroke="url(#cortexBody)" strokeWidth="2.5" strokeLinecap="round" />
          <line x1="90" y1="35" x2="90" y2="10" stroke="url(#cortexBody)" strokeWidth="3" strokeLinecap="round" />
          <line x1="105" y1="35" x2="110" y2="16" stroke="url(#cortexBody)" strokeWidth="2.5" strokeLinecap="round" />
          <motion.circle cx="70" cy="13" r="4" fill="#A84397" filter="url(#cortexGlow)"
            animate={{ opacity: [0.5, 1, 0.5] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.circle cx="90" cy="7" r="5" fill="#D23CB3" filter="url(#cortexBigGlow)"
            animate={{ opacity: [0.6, 1, 0.6], scale: [1, 1.2, 1] }}
            transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
          />
          <motion.circle cx="110" cy="13" r="4" fill="#A84397" filter="url(#cortexGlow)"
            animate={{ opacity: [1, 0.5, 1] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
          />

          {/* Arms — stronger, armored */}
          <motion.g
            animate={{ rotate: [0, 8, 0, -5, 0] }}
            transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
            style={{ transformOrigin: '55px 125px' }}
          >
            <rect x="22" y="118" width="35" height="16" rx="8" fill="url(#cortexDark)" />
            <circle cx="24" cy="126" r="9" fill="url(#cortexBody)" stroke="#D23CB3" strokeWidth="0.5" strokeOpacity="0.3" />
            <circle cx="24" cy="126" r="4" fill="#D23CB3" opacity="0.3" />
          </motion.g>
          <motion.g
            animate={{ rotate: [0, -8, 0, 5, 0] }}
            transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut', delay: 0.5 }}
            style={{ transformOrigin: '125px 125px' }}
          >
            <rect x="123" y="118" width="35" height="16" rx="8" fill="url(#cortexDark)" />
            <circle cx="156" cy="126" r="9" fill="url(#cortexBody)" stroke="#D23CB3" strokeWidth="0.5" strokeOpacity="0.3" />
            <circle cx="156" cy="126" r="4" fill="#D23CB3" opacity="0.3" />
          </motion.g>

          {/* Legs */}
          <rect x="65" y="193" width="16" height="22" rx="7" fill="url(#cortexDark)" />
          <rect x="99" y="193" width="16" height="22" rx="7" fill="url(#cortexDark)" />
          <ellipse cx="73" cy="216" rx="12" ry="5" fill="url(#cortexBody)" />
          <ellipse cx="107" cy="216" rx="12" ry="5" fill="url(#cortexBody)" />
        </svg>

        {/* Logo dentro del visor (viewBox 180×240: rect visor x=56 y=45 w=68 h=45) */}
        <div
          className="pointer-events-none absolute flex items-center justify-center"
          style={{
            left: `${(56 / 180) * 100}%`,
            top: `${(45 / 240) * 100}%`,
            width: `${(68 / 180) * 100}%`,
            height: `${(45 / 240) * 100}%`,
          }}
        >
          <motion.img
            src="/logo-icon.png"
            alt=""
            className="max-h-[58%] max-w-[58%] object-contain select-none"
            style={{ filter: 'drop-shadow(0 0 10px rgba(210,60,179,0.6))' }}
            animate={{ scale: [1, 1.06, 1] }}
            transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
          />
        </div>
      </motion.div>

      <div className="text-center mt-3">
        <p className="text-xl font-black font-headline tracking-widest gradient-text">CORTEX</p>
      </div>
    </motion.div>
  );
}

export default function AIMascots() {
  const { t } = useTranslation();

  return (
    <section className="relative py-20 md:py-28 overflow-hidden bg-transparent">
      <StarField starCount={250} speed={0.3} />
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] rounded-full blur-[130px] opacity-[0.05]" style={{ background: 'radial-gradient(ellipse, #D23CB3, #A84397, transparent)' }} />

      <div className="mx-auto min-w-0 max-w-6xl px-4 sm:px-6">
        <motion.div
          className="text-center mb-14"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-4xl md:text-5xl font-black font-headline tracking-tight">
            {t('aiTeam.title')} <span className="gradient-text">{t('aiTeam.titleHighlight')}</span>
          </h2>
          <p className="text-on-surface-variant text-base md:text-lg mt-4 max-w-xl mx-auto">
            {t('aiTeam.subtitle')}
          </p>
        </motion.div>

        {/* CORTEX — centered, larger, special */}
        <div className="flex justify-center mb-14">
          <CortexBot />
        </div>

        {/* Connection lines from CORTEX to agents */}
        <motion.div
          className="text-center mb-6"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <div className="mx-auto" style={{ width: '60%', maxWidth: '500px', height: '1px', background: 'linear-gradient(90deg, transparent, rgba(210,60,179,0.3), transparent)' }} />
          <p className="text-[10px] text-on-surface-variant/40 font-label uppercase tracking-[4px] mt-3">{t('aiTeam.agentsLabel')}</p>
        </motion.div>

        {/* 6 Agents grid */}
        <div className="grid min-w-0 grid-cols-2 gap-6 md:grid-cols-3 md:gap-4 lg:grid-cols-6">
          {agents.map((agent, i) => (
            <AgentBot key={agent.name} agent={agent} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}
